import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import type Highcharts from "highcharts/es-modules/masters/highcharts.src.js";
import { Chart, type HighchartsReactRefObject } from "@highcharts/react";
import { useFetch } from "../../../../hooks/api";
import { keepPreviousData } from "@tanstack/react-query";
import { Col, Card, Row, Nav } from "react-bootstrap";
import { useAppSelector } from "../../../../redux/hooks";
import { useCategoriesQuery } from "../../../../hooks/useCategories";
import { Tooltip, TooltipTrigger } from "react-aria-components";
import { Button } from "../../../../components/core/basic/Button";
import { css } from "../../../../../styled-system/css";
import { TagSelectorModal } from "../../../../modules/tags/TagSelector";
import { CategorySelectorModal } from "../../../../modules/categories/CategorySelector";
import { useModal } from "@ebay/nice-modal-react";
import { Flex, Center } from "../../../../../styled-system/jsx";
import BreadcrumbDates, { type Breadcrumb } from "../../../../components/core/blocks/BreadcrumbDates";
import { BUCKET_COLORS } from "../../../../modules/pricingv2/utils";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import PricingPlanTable, { type DashboardStats } from "../../../../modules/pricingv2/PricingPlanTable";
import { DASHBOARD_LINKS } from "../../../../utils/dashboardLinks";
import TrellisNavLink from "../../../../components/core/basic/NavLink";
import Loading from "../../../../components/core/blocks/Loading";
import PriceLog from "../../../../modules/pricingv2/components/PriceLog";
import { trellisPalette } from "../../../../components/custom/analytics/palettes";
import { ButtonGroup, type ButtonDefinition } from "../../../../components/core/basic/ButtonGroup";
import { formatNumber, formatCurrency, formatPercent } from "../../../../utils/formatNumber";
import { BsFillFilterSquareFill } from "react-icons/bs";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import moment from "moment";

interface ScatterPoint {
  type: "scatter";
  name: string;
  data: [number, number][];
  color: string;
}

interface BucketSeries {
  type: "column";
  name: string;
  data: number[]; // count of items in the bucket
  color: string;
}

interface PlanStats {
  product_id: number;
  pricing_plan_id: number;
  bucket: string;
  sales_percentage_diff: number;
  asp_to_budgeted_price: number;
  forecasted_units: number;
  actual_units: number;
  forecasted_sales: number;
  actual_sales: number;
  category_id: number;
  tags: string[];
  plan_name: string;
  best_price: number;
  status: string;
  reference_price: number;
  current_program?: { template_type: string };
  current_program__template_type?: string | null;
  plan_type: string;
  enroll_date?: string;
  is_good_plan: boolean;
}

interface HighchartsSeries {
  name: string;
  data: number[];
  categories: string[];
  plans: Record<string, number[]>;
}

type Buckets = "performance" | "deviation";

const Forecast = () => {
  const { t } = useTranslation(["pricingv2"]);
  const hasPriceDebugModule = useModuleEnabled("PRICE_DEBUG");
  const scatterChartRef = useRef<HighchartsReactRefObject>(null);
  const bucketChartRef = useRef<HighchartsReactRefObject>(null);
  const salesPercentageChartRef = useRef<HighchartsReactRefObject>(null);
  const aspToBudgetedChartRef = useRef<HighchartsReactRefObject>(null);
  const tagSelectorModal = useModal(TagSelectorModal);
  const categorySelectorModal = useModal(CategorySelectorModal);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tab, setTab] = useState("plans");
  const [selectedBucket, setSelectedBucket] = useState<Buckets>("performance");
  const [filteredBucket, setFilteredBucket] = useState<number[] | undefined>();

  const { yesterday, dayAtStartOfMonth, dayAtEndOfMonth } = useMemo(() => {
    const today = moment();
    return {
      yesterday: today.subtract(1, "days").format("YYYY-MM-DD"),
      dayAtStartOfMonth: today.subtract(1, "days").format("YYYY-MM-DD"),
      dayAtEndOfMonth: today.endOf("month").format("YYYY-MM-DD"),
    };
  }, []);

  const breadCrumbItems: Breadcrumb[] = [
    {
      name: t(($) => $.pricingv2["Pricing Dashboard"]),
      href: "/user/pricing/dashboard",
      dropdownLinkOptions: DASHBOARD_LINKS,
      unsorted: true,
      searchable: false,
      dropdownSubtitle: t(($) => $.pricingv2["Dashboards"]),
    },
  ];

  const customerForecast = useFetch<{
    org_id: number;
    plan_stats: PlanStats[];
  }>(
    ["customer_forecast", selectedCategories, selectedTags],
    `/pricing/customer_forecast`,
    {
      marketPlace: marketPlace,
      date: yesterday,
    },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const priceChanges = useFetch<{
    price_increases: number;
    price_decreases: number;
  }>(
    ["pricing_change", filteredBucket],
    `/pricing/price_change`,
    {
      market_place: marketPlace,
      plan_ids: filteredBucket && filteredBucket.length > 0 ? filteredBucket.join(",") : "",
      end_date: dayAtEndOfMonth,
      start_date: dayAtStartOfMonth,
    },
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
    },
  );

  const buttunBuckets: ButtonDefinition<Buckets>[] = [
    { label: t(($) => $.pricingv2["Performance Buckets"]), value: "performance", counter: undefined },
    { label: t(($) => $.pricingv2["Deviation Analysis"]), value: "deviation", counter: undefined },
  ];

  const {
    scatterOptions,
    bucketOptions,
    tags,
    tagCount,
    pricingPlans,
    categoryCount,
    salesPercentageOptions,
    aspToBudgetedOptions,
    incUnits,
    incCashflow,
    noForecastCount,
    noSalesCount,
    goodCount,
    aspToBudgetPrice,
    salesPercentageDiff,
    totalProductCount,
  }: {
    scatterOptions: Highcharts.Options;
    bucketOptions: Highcharts.Options;
    tags: string[];
    tagCount: Record<string, number>;
    pricingPlans: DashboardStats[];
    categoryCount: Record<number, number>;
    salesPercentageOptions: Highcharts.Options;
    aspToBudgetedOptions: Highcharts.Options;
    incUnits: number;
    incCashflow: number;
    noForecastCount: number[];
    noSalesCount: number[];
    goodCount?: number[];
    aspToBudgetPrice: number;
    salesPercentageDiff: number;
    totalProductCount: number;
  } = useMemo(() => {
    const tagCount: Record<string, number> = {};
    const categoryCount: Record<number, number> = {};
    if (!customerForecast.data) {
      return {
        scatterOptions: {},
        bucketOptions: {},
        tags: [],
        tagCount: {},
        pricingPlans: [],
        categoryCount: {},
        salesPercentageOptions: {},
        aspToBudgetedOptions: {},
        incContribution: 0,
        incUnits: 0,
        incCashflow: 0,
        noForecastCount: [],
        noSalesCount: [],
        goodCount: [],
        aspToBudgetPrice: 0,
        salesPercentageDiff: 0,
        totalProductCount: 0,
      };
    }

    const chartProducts: Record<string, number[]> = {};
    const scatterSeries: Record<string, ScatterPoint> = {};
    const bucketSeries: Record<string, BucketSeries> = {};
    const tags: string[] = [];
    const pricingPlanIds: number[] = [];
    const salesPercentages: number[][] = [];
    const aspToBudgetedPrices: number[][] = [];
    const donePlans: number[] = [];
    let incUnits = 0;
    let incCashflow = 0;
    let aspToBudgetPrice = 0;
    let salesPercentageDiff = 0;
    const noForecastCount: number[] = [];
    const noForecastPriceCount: number[] = [];
    const noSalesCount: number[] = [];
    const goodCount: number[] = [];
    const pricingPlans: DashboardStats[] = [];
    let totalProductCount = 0;

    for (const ps of customerForecast.data.plan_stats) {
      let bucket = ps.bucket;
      const isGoodPlan = ps.is_good_plan;

      if (bucket === "undefined" || bucket === "null") {
        bucket = "No Forecast";
      }

      for (const tag of ps.tags) {
        if (!tags.includes(tag)) {
          tags.push(tag);
        }
        if (tagCount[tag] === undefined) {
          tagCount[tag] = 0;
        }
        tagCount[tag]++;
      }

      if (selectedTags.length > 0 && !ps.tags.some((tag) => selectedTags.includes(tag))) {
        continue; // Skip if the tag is not selected
      }

      totalProductCount++;

      const planId = ps.pricing_plan_id;
      if (filteredBucket && !filteredBucket.includes(planId)) {
        continue;
      }

      pricingPlanIds.push(planId);

      if (donePlans.includes(planId)) {
        continue; // Skip if the plan has already been processed
      }

      if (chartProducts[bucket] === undefined) {
        chartProducts[bucket] = [];
      }

      if (bucket !== "No Forecast" && bucket !== "No Forecast Price" && bucket !== "No Sales") {
        if (scatterSeries[bucket] === undefined) {
          scatterSeries[bucket] = {
            type: "scatter",
            name: bucket,
            data: [],
            color: BUCKET_COLORS[bucket] || "rgba(0,0,0,0.55)",
          };
        }

        if (bucketSeries[bucket] === undefined) {
          bucketSeries[bucket] = {
            type: "column",
            name: bucket,
            data: [0],
            color: BUCKET_COLORS[bucket] || "rgba(0,0,0,0.55)",
          };
        }
        salesPercentages.push([planId, ps.sales_percentage_diff]);
        aspToBudgetedPrices.push([planId, ps.asp_to_budgeted_price]);

        chartProducts[bucket]!.push(planId);
        scatterSeries[bucket]!.data.push([ps.sales_percentage_diff, ps.asp_to_budgeted_price]);

        bucketSeries[bucket]!.data[0]!++;
      } else if (bucket === "No Forecast") {
        noForecastCount.push(planId);
      } else if (bucket === "No Forecast Price") {
        noForecastPriceCount.push(planId);
      } else {
        noSalesCount.push(planId);
      }

      if (isGoodPlan) {
        goodCount.push(planId);
      }

      incCashflow += ps.actual_sales - parseFloat(ps.forecasted_sales.toFixed(2));
      incUnits += ps.actual_units - parseInt(ps.forecasted_units.toFixed(0));
      aspToBudgetPrice += ps.asp_to_budgeted_price;
      salesPercentageDiff += ps.sales_percentage_diff;
      const ds: DashboardStats = {
        plan_id: ps.pricing_plan_id,
        id: ps.pricing_plan_id,
        bucket: ps.bucket,
        sales_percentage_diff: ps.sales_percentage_diff - 1.0,
        asp_to_budgeted_price: ps.asp_to_budgeted_price - 1.0,
        forecasted_units: parseFloat(ps.forecasted_units.toFixed(0)),
        actual_units: parseFloat(ps.actual_units.toFixed(2)),
        forecasted_sales: ps.forecasted_sales,
        actual_sales: ps.actual_sales,
        category_id: ps.category_id,
        tags: ps.tags,
        plan_name: ps.plan_name,
        best_price: ps.best_price,
        reference_price: ps.reference_price,
        status: ps.status === "enabled" ? "enabled" : "disabled",
        asp: ps.actual_sales / ps.actual_units || 0,
        current_program: ps.current_program,
        current_program__template_type: ps.current_program?.template_type || null,
        plan_type: ps.plan_type,
        enroll_date: ps.enroll_date,
        // Add default values for missing DashboardStats properties
        inc_cashflow: 0,
        inc_contribution: 0,
        inc_price: 0,
        inc_units: 0,
        name: ps.plan_name,
        price_decreased: 0,
        price_decreased_avg: 0,
        price_increased: 0,
        price_increased_avg: 0,
        reference_cr: 0,

        target_sales: 0,
        target_units: 0,
        total_sales: 0,
        total_units: 0,
        page_views: 0,
        value: 0,
      };
      pricingPlans.push(ds);
      donePlans.push(planId);
    }

    if (donePlans.length > 0) {
      aspToBudgetPrice = parseFloat((aspToBudgetPrice / donePlans.length).toFixed(2)) - 1.0;
      salesPercentageDiff = parseFloat((salesPercentageDiff / donePlans.length).toFixed(2)) - 1.0;
    }

    let xMax = 0;
    let yMax = 0;
    let xMin = 9999;
    let yMin = 9999;
    for (const s of Object.values(scatterSeries)) {
      for (const p of s.data) {
        if (p[0] > xMax) xMax = p[0];
        if (p[1] > yMax) yMax = p[1];
        if (p[0] < xMin) xMin = p[0];
        if (p[1] < yMin) yMin = p[1];
      }
    }

    if (xMax > 2) xMax = 2;
    if (yMax > 2) yMax = 2;
    if (xMin < 0) xMin = 0;
    if (yMin < 0) yMin = 0;

    const scatterOptions: Highcharts.Options = {
      chart: {
        type: "scatter",
        style: {
          fontFamily: "Poppins",
          fontWeight: "300",
          fontSize: "15px",
        },
      },
      title: { text: undefined },
      xAxis: { min: xMin, max: xMax, tickInterval: 0.1, title: { text: t(($) => $.pricingv2["Sales Forecast"]) } },
      yAxis: { title: { text: t(($) => $.pricingv2["Price"]) }, min: yMin, max: yMax, tickInterval: 0.1 },
      plotOptions: {
        scatter: {
          showInLegend: true,
          jitter: { x: 0, y: 0 },
          marker: { radius: 4, symbol: "circle" },
          tooltip: {
            pointFormat: t(($) => $.pricingv2["Sales Price: {x} - Price: {y}"], {
              x: "{point.x:.3f}",
              y: "{point.y:.3f}",
            }),
          },
          point: {
            events: {
              click: function (event) {
                const seriesName = event.point.series.name;
                setFilteredBucket(chartProducts[seriesName]);
              },
            },
          },
        },
      },
      series: Object.values(scatterSeries),
    };

    const bucketOptions: Highcharts.Options = {
      chart: {
        type: "column",
        height: "350px",
        style: {
          fontFamily: "Poppins",
          fontWeight: "300",
          fontSize: "15px",
        },
      },
      title: {
        text: undefined,
      },
      xAxis: {
        visible: false,
      },
      yAxis: {
        min: 0,
        title: {
          text: t(($) => $.pricingv2["# of Products"]),
        },
      },
      plotOptions: {
        column: {
          pointPadding: 0.2,
          borderWidth: 0,
        },
        series: {
          groupPadding: 0,
          events: {
            click: function (event) {
              const seriesName = event.point.series.name;
              setFilteredBucket(chartProducts[seriesName]);
            },
          },
        },
      },
      series: Object.values(bucketSeries),
    };

    const salesPercentageData = bucketSalesPercentageDiff(
      t(($) => $.pricingv2["Sales % Difference"]),
      salesPercentages,
    );
    const salesPercentageOptions: Highcharts.Options = {
      chart: {
        type: "column",
        style: {
          fontFamily: "Poppins",
          fontWeight: "300",
          fontSize: "15px",
        },
      },
      title: {
        text: undefined,
      },
      xAxis: {
        categories: salesPercentageData.categories,
        crosshair: true,
      },
      yAxis: {
        min: 0,
        title: {
          text: t(($) => $.pricingv2["Number of Products"]),
        },
      },
      tooltip: {
        valueSuffix: ` ${t(($) => $.pricingv2["products"])}`,
      },
      legend: {
        enabled: true,
      },
      plotOptions: {
        column: {
          pointPadding: 0.2,
          borderWidth: 0,
        },
        series: {
          groupPadding: 0,
          events: {
            click: function (event: Highcharts.SeriesClickEventObject) {
              const category_name = event.point.category;
              setFilteredBucket(salesPercentageData.plans[category_name]);
            },
          },
        },
      },
      series: [
        {
          type: "column",
          name: t(($) => $.pricingv2["Forecast Buckets"]),
          data: salesPercentageData.data,
          color: trellisPalette[3],
        },
      ],
    };

    const aspToBudgetedPriceData = bucketSalesPercentageDiff(
      t(($) => $.pricingv2["ASP to Budgeted Price"]),
      aspToBudgetedPrices,
    );
    const aspToBudgetedOptions: Highcharts.Options = {
      chart: {
        type: "column",
        style: {
          fontFamily: "Poppins",
          fontWeight: "300",
          fontSize: "15px",
        },
      },
      title: {
        text: undefined,
      },
      xAxis: {
        categories: aspToBudgetedPriceData.categories,
        crosshair: true,
      },
      yAxis: {
        min: 0,
        title: {
          text: t(($) => $.pricingv2["Number of Products"]),
        },
      },
      tooltip: {
        valueSuffix: ` ${t(($) => $.pricingv2["products"])}`,
      },
      legend: {
        enabled: true,
      },
      plotOptions: {
        column: {
          pointPadding: 0.2,
          borderWidth: 0,
        },
        series: {
          groupPadding: 0,
          events: {
            click: function (event: Highcharts.SeriesClickEventObject) {
              const category_name = event.point.category;
              setFilteredBucket(aspToBudgetedPriceData.plans[category_name]);
            },
          },
        },
      },
      series: [
        {
          type: "column",
          name: t(($) => $.pricingv2["Forecast Buckets"]),
          data: aspToBudgetedPriceData.data,
          color: trellisPalette[0],
        },
      ],
    };

    return {
      scatterOptions,
      bucketOptions,
      tags,
      tagCount,
      pricingPlans,
      categoryCount,
      salesPercentageOptions,
      aspToBudgetedOptions,
      incUnits,
      incCashflow,
      noForecastCount,
      noSalesCount,
      goodCount,
      aspToBudgetPrice,
      salesPercentageDiff,
      totalProductCount,
    };
  }, [customerForecast, selectedTags, filteredBucket, setFilteredBucket, t]);

  useEffect(() => {
    const bucketChart = bucketChartRef.current?.chart;
    const scatterChart = scatterChartRef.current?.chart;

    if (!bucketChart || !scatterChart) return;

    interface SyncLegendToggleChart {
      series: Highcharts.Series[];
      redraw: () => void;
    }

    const syncLegendToggle = (chartFrom: SyncLegendToggleChart, chartTo: SyncLegendToggleChart): void => {
      chartFrom.series.forEach((series: Highcharts.Series, index: number) => {
        series.update(
          {
            events: {
              legendItemClick(this: Highcharts.Series): void {
                const seriesToToggle = chartTo.series[index];
                if (seriesToToggle) {
                  if (series.visible) {
                    seriesToToggle.hide();
                  } else {
                    seriesToToggle.show();
                  }
                }
              },
            },
          } as unknown as Highcharts.SeriesOptionsType,
          false,
        );
      });

      chartFrom.redraw();
    };

    // Synchronize both ways
    syncLegendToggle(bucketChart, scatterChart);
    syncLegendToggle(scatterChart, bucketChart);
  }, [bucketOptions, scatterOptions]);

  return (
    <>
      <BreadcrumbDates items={breadCrumbItems} hideDateSelector></BreadcrumbDates>
      <PageContainer withBreadcrumb padding={"large"}>
        {customerForecast.isLoading && <Loading fullPage={false} height={"300px"} />}
        {customerForecast.isError && (
          <Row className="m-2">
            <Col className="mx-3 my-5 py-5 text-center" xs={12}>
              {t(($) => $.pricingv2["Error..."])}
            </Col>
          </Row>
        )}

        {!customerForecast.isLoading && (
          <>
            <Flex
              gap="small"
              justifyContent={"space-between"}
              alignContent={"center"}
              paddingBottom={"medium"}
              wrap="wrap"
            >
              <Flex
                gap="small"
                justifyContent={"space-between"}
                alignContent={"center"}
                paddingBottom={"medium"}
                wrap="wrap"
              >
                <ButtonGroup
                  buttons={buttunBuckets}
                  value={selectedBucket}
                  onSelect={(values) => {
                    setSelectedBucket(values);
                  }}
                />
              </Flex>
              {filteredBucket && (
                <Flex
                  gap="small"
                  justifyContent={"space-between"}
                  alignContent={"center"}
                  paddingBottom={"medium"}
                  wrap="wrap"
                >
                  <span className="fs-4 fw-bold">
                    {t(($) => $.pricingv2["{count} Products in Bucket."], { count: filteredBucket.length })}
                  </span>
                  <span
                    className="fs-4 fw-bold"
                    style={{ cursor: "pointer", color: trellisPalette[0] }}
                    onClick={() => {
                      setFilteredBucket(undefined);
                    }}
                  >
                    {t(($) => $.pricingv2["Clear Filter"])}
                  </span>
                </Flex>
              )}

              <Flex
                gap="small"
                justifyContent={"space-between"}
                alignContent={"center"}
                paddingBottom={"medium"}
                wrap="wrap"
              >
                <TagSelectorButton
                  onClick={() => {
                    void tagSelectorModal.show({
                      initialSelectedTags: selectedTags,
                      onConfirm: (value) => {
                        setSelectedTags(value);
                      },
                      getIrrelevantTagsHiddenText: (num) =>
                        t(($) => $.pricingv2["{count} tags without plans not shown"], { count: num }),
                      sortTags: (a, b) => {
                        const aCnt = tagCount[a] ?? 0;
                        const bCnt = tagCount[b];
                        if (aCnt && bCnt) {
                          return bCnt - aCnt;
                        } else if (aCnt) {
                          return -1;
                        } else if (bCnt) {
                          return 1;
                        }
                        return 0;
                      },
                      hideIrrelevantTags: () => {
                        return false;
                      },
                      tags: tags,
                      tagCount: tagCount,
                    });
                  }}
                  seleectedTags={selectedTags}
                  tags={tags}
                />
                <CategorySelectorButton
                  onClick={() => {
                    void categorySelectorModal.show({
                      initialSelectedCategories: selectedCategories,
                      onConfirm: (value) => {
                        setSelectedCategories(value);
                      },

                      getIrrelevantCategoriesHiddenText: (num) =>
                        t(($) => $.pricingv2["{count} categories without plans not shown"], { count: num }),
                      sortCategories: (a, b) => {
                        const aCnt = categoryCount[a.id] ?? 0;
                        const bCnt = categoryCount[b.id];
                        if (aCnt && bCnt) {
                          return bCnt - aCnt;
                        } else if (aCnt) {
                          return -1;
                        } else if (bCnt) {
                          return 1;
                        }
                        return 0;
                      },
                      hideIrrelevantCategories: (category) => {
                        if (categoryCount[category.id]) {
                          return false;
                        }
                        return true;
                      },
                      getDescription: (category) => {
                        if (categoryCount[category.id]) {
                          return t(($) => $.pricingv2["Number of plans: {count}"], {
                            count: categoryCount[category.id],
                          });
                        }
                        return "";
                      },
                    });
                  }}
                  selectedCategories={selectedCategories}
                />
              </Flex>
            </Flex>

            {hasPriceDebugModule && !priceChanges.isLoading && (
              <Row className="mx-3 mb-3">
                <Col xs={12}>
                  <Row className="text-center">
                    <Col className="mb-3" xs={6}>
                      <div className="fs-standard">{t(($) => $.pricingv2["Price Increases"])}</div>
                      <div className="fw-bold" style={{ fontSize: "3rem" }}>
                        {formatNumber(priceChanges.data?.price_increases, { decimalPlaces: 0 })}
                      </div>
                    </Col>
                    <Col className="mb-3" xs={6}>
                      <div className="fs-standard">{t(($) => $.pricingv2["Price Decreases"])}</div>
                      <div className="fw-bold" style={{ fontSize: "3rem" }}>
                        {formatNumber(priceChanges.data?.price_decreases, { decimalPlaces: 0 })}
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            )}
            <Row className="mx-3 mb-3">
              <Col>
                <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
                  <Card.Body className="p-4">
                    <Row className="text-center fs-standard">
                      <Col>
                        <div className="fs-4">{t(($) => $.pricingv2["Price Deviation"])}</div>
                        <div className="fw-bold" style={{ fontSize: "3rem" }}>
                          {customerForecast.isFetching ? "-" : formatPercent(aspToBudgetPrice)}
                        </div>
                      </Col>
                      <Col>
                        <div className="fs-4">{t(($) => $.pricingv2["Forecast Deviation"])}</div>
                        <div className="fw-bold" style={{ fontSize: "3rem" }}>
                          {customerForecast.isFetching ? "-" : formatPercent(salesPercentageDiff)}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              <Col>
                <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
                  <Card.Body className="p-4">
                    <Row className="text-center fs-standard">
                      {filteredBucket !== undefined && (
                        <Col>
                          <div className="fs-4">{t(($) => $.pricingv2["Selected Products"])}</div>
                          <div className="fw-bold" style={{ fontSize: "3rem" }}>
                            {customerForecast.isFetching ?
                              "-"
                            : formatNumber(filteredBucket.length, { decimalPlaces: 0 })}
                          </div>
                        </Col>
                      )}
                      <Col>
                        <div className="fs-4">{t(($) => $.pricingv2["# Products"])}</div>
                        <div className="fw-bold" style={{ fontSize: "3rem" }}>
                          {customerForecast.isFetching ? "-" : formatNumber(totalProductCount, { decimalPlaces: 0 })}
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
              <Col>
                <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
                  <Card.Body className="p-4">
                    <Row className="text-center fs-standard">
                      <Col>
                        {incCashflow > 0 ?
                          <div className="fs-4">{t(($) => $.pricingv2["Sales Above Forecast"])}</div>
                        : <div className="fs-4">{t(($) => $.pricingv2["Sales Below Forecast"])}</div>}
                        <div className={`fw-bold`} style={{ color: "#000", fontSize: "3rem" }}>
                          {customerForecast.isFetching ?
                            "-"
                          : formatCurrency(incCashflow, {
                              marketPlace: marketPlace,
                              compact: true,
                              addPlus: true,
                            })
                          }
                        </div>
                      </Col>
                      <Col>
                        {incUnits > 0 ?
                          <div className="fs-4">{t(($) => $.pricingv2["Units Above Forecast"])}</div>
                        : <div className="fs-4">{t(($) => $.pricingv2["Units Below Forecast"])}</div>}
                        <div className="fw-bold" style={{ color: "#000", fontSize: "3rem" }}>
                          {customerForecast.isFetching ?
                            "-"
                          : formatNumber(incUnits, {
                              addPlus: true,
                            })
                          }
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            <Row className="mx-3 text-center">
              <Col xs={12}>
                {selectedBucket === "performance" && (
                  <Row>
                    <Col xs={5}>
                      <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
                        <Card.Body className="p-4">
                          <Flex
                            gap="small"
                            justifyContent={"space-between"}
                            alignContent={"center"}
                            paddingBottom={"medium"}
                            wrap="wrap"
                          >
                            <div className="fs-4 fw-bold">
                              {t(($) => $.pricingv2["# of Products by Forecast Bucket"])}
                            </div>
                          </Flex>
                          <Chart options={bucketOptions} />

                          <Row>
                            <Col>
                              <div className="fs-standard mt-2 text-start">
                                <span className="ms-3">
                                  {t(($) => $.pricingv2["Products with no forecast:"])}{" "}
                                  <span className="fw-bold">{noForecastCount.length}</span>
                                  {noForecastCount.length > 0 && (
                                    <span
                                      className="ps-2"
                                      style={{ color: trellisPalette[0], cursor: "pointer" }}
                                      onClick={() => {
                                        setFilteredBucket(noForecastCount);
                                      }}
                                    >
                                      <BsFillFilterSquareFill />
                                    </span>
                                  )}
                                </span>
                              </div>

                              <div className="fs-standard mt-2 text-start">
                                <span className="ms-3">
                                  {t(($) => $.pricingv2["Products with no sales:"])}{" "}
                                  <span className="fw-bold">{noSalesCount.length}</span>
                                  {noSalesCount.length > 0 && (
                                    <span
                                      className="ps-2"
                                      style={{ color: trellisPalette[0], cursor: "pointer" }}
                                      onClick={() => {
                                        setFilteredBucket(noSalesCount);
                                      }}
                                    >
                                      <BsFillFilterSquareFill />
                                    </span>
                                  )}
                                </span>
                              </div>
                            </Col>
                            <Col>
                              <div className="fs-standard mt-2 text-start">
                                <span className="ms-3">
                                  {t(($) => $.pricingv2["Products in good region:"])}{" "}
                                  <span className="fw-bold">{goodCount.length}</span>
                                  {goodCount.length > 0 && (
                                    <span
                                      className="ps-2"
                                      style={{ color: trellisPalette[0], cursor: "pointer" }}
                                      onClick={() => {
                                        setFilteredBucket(goodCount);
                                      }}
                                    >
                                      <BsFillFilterSquareFill />
                                    </span>
                                  )}
                                </span>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xs={7}>
                      <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
                        <Card.Body className="p-4">
                          <Flex
                            gap="small"
                            justifyContent={"space-between"}
                            alignContent={"center"}
                            paddingBottom={"medium"}
                            wrap="wrap"
                          >
                            <div className="fs-4 fw-bold">{t(($) => $.pricingv2["Products by Forecast Bucket"])}</div>
                          </Flex>
                          <Chart options={scatterOptions} ref={scatterChartRef} />
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}
                {selectedBucket === "deviation" && (
                  <Row>
                    <Col xs={6}>
                      <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
                        <Card.Body className="p-4">
                          <Flex
                            gap="small"
                            justifyContent={"space-between"}
                            alignContent={"center"}
                            paddingBottom={"medium"}
                            wrap="wrap"
                          >
                            <div className="fs-4 fw-bold">{t(($) => $.pricingv2["Price Forecast Accuracy"])}</div>
                          </Flex>
                          <Chart options={aspToBudgetedOptions} ref={aspToBudgetedChartRef} />
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xs={6}>
                      <Card style={{ borderRadius: "0.5rem" }} className="mb-4">
                        <Card.Body className="p-4">
                          <Flex
                            gap="small"
                            justifyContent={"space-between"}
                            alignContent={"center"}
                            paddingBottom={"medium"}
                            wrap="wrap"
                          >
                            <div className="fs-4 fw-bold">{t(($) => $.pricingv2["Units Forecast Accuracy"])}</div>
                          </Flex>

                          <Chart options={salesPercentageOptions} ref={salesPercentageChartRef} />
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                )}
              </Col>
              <Col className="mt-5 ph-4 mb-3 border-bottom fs-standard" xs={12}>
                <Nav
                  variant="underline"
                  onSelect={(k) => {
                    if (k !== null) {
                      setTab(k);
                    }
                  }}
                  activeKey={tab}
                >
                  <TrellisNavLink eventKey="plans" className="text-start">
                    {t(($) => $.pricingv2["Pricing Plans"])}
                  </TrellisNavLink>
                  <TrellisNavLink eventKey="price_changes" className="text-start">
                    {t(($) => $.pricingv2["Price Changes"])}
                  </TrellisNavLink>
                </Nav>
              </Col>
              <Col className="pt-5" xs={12}>
                {tab === "plans" && (
                  <Row>
                    <Col className="py-0">
                      <PricingPlans pricingPlans={pricingPlans} />
                    </Col>
                  </Row>
                )}
                {tab === "price_changes" && (
                  <Row>
                    <Col className="py-0">
                      <PriceLog tabList={["price_increases", "price_decreases"]} vertical />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </>
        )}
      </PageContainer>
    </>
  );
};

function PricingPlans({ pricingPlans }: { pricingPlans: DashboardStats[] }) {
  const { t } = useTranslation(["pricingv2"]);
  if (pricingPlans.length === 0) {
    return (
      <Center marginTop="large" color="text.secondary" fontSize={"xxlarge"} textAlign={"center"}>
        {t(($) => $.pricingv2["No Pricing Plans match your selection"])}
      </Center>
    );
  }

  return <PricingPlanTable pricingPlans={pricingPlans} fromForecast />;
}

function CategorySelectorButton({
  onClick,
  selectedCategories,
}: {
  onClick: () => void;
  selectedCategories: number[];
}) {
  const { t } = useTranslation(["pricingv2"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const categoryQuery = useCategoriesQuery(marketPlace, channel);
  return (
    <TooltipTrigger>
      <Button onPress={onClick} variant={"primary"}>
        {selectedCategories.length > 0 ?
          t(($) => $.pricingv2["Select Categories ({count})"], { count: selectedCategories.length })
        : t(($) => $.pricingv2["Select Categories"])}
      </Button>
      <Tooltip
        placement={"bottom"}
        className={css({
          border: "1px solid {colors.lightgrey}",
          borderRadius: "light",
          padding: "medium",
          fontSize: "medium",
          backgroundColor: "white",
        })}
        offset={10}
      >
        {selectedCategories.length ?
          <figure>
            <figcaption>{t(($) => $.pricingv2["Selected Categories:"])}</figcaption>
            <ul>
              {categoryQuery.data
                ?.filter((c) => selectedCategories.includes(c.id))
                .map((category) => (
                  <li key={category.id}>{category.leaf_category_name}</li>
                ))}
            </ul>
          </figure>
        : <p
            className={css({
              margin: "0",
              verticalAlign: "middle",
            })}
          >
            {t(($) => $.pricingv2["Showing all categories"])}
          </p>
        }
      </Tooltip>
    </TooltipTrigger>
  );
}

function TagSelectorButton({
  onClick,
  seleectedTags,
  tags,
}: {
  onClick: () => void;
  seleectedTags: string[];
  tags: string[];
}) {
  const { t } = useTranslation(["pricingv2"]);
  return (
    <TooltipTrigger>
      <Button onPress={onClick} variant={"primary"}>
        {seleectedTags.length > 0 ?
          t(($) => $.pricingv2["Select Tags ({count})"], { count: seleectedTags.length })
        : t(($) => $.pricingv2["Select Tags"])}
      </Button>
      <Tooltip
        placement={"bottom"}
        className={css({
          border: "1px solid {colors.lightgrey}",
          borderRadius: "light",
          padding: "medium",
          fontSize: "medium",
          backgroundColor: "white",
        })}
        offset={10}
      >
        {seleectedTags.length ?
          <figure>
            <figcaption>{t(($) => $.pricingv2["Selected Tags:"])}</figcaption>
            <ul>
              {tags
                .filter((c) => seleectedTags.includes(c))
                .map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
            </ul>
          </figure>
        : <p
            className={css({
              margin: "0",
              verticalAlign: "middle",
            })}
          >
            {t(($) => $.pricingv2["Showing all tags"])}
          </p>
        }
      </Tooltip>
    </TooltipTrigger>
  );
}

function bucketSalesPercentageDiff(name: string, values: number[][]): HighchartsSeries {
  const buckets: { label: string; min: number; max: number }[] = [
    { label: "-100 to -95%", min: 0.0, max: 0.05 },
    { label: "-95% to -85%", min: 0.05, max: 0.15 },
    { label: "-85% to -75%", min: 0.15, max: 0.25 },
    { label: "-75% to -65%", min: 0.25, max: 0.35 },
    { label: "-65% to -55%", min: 0.35, max: 0.45 },
    { label: "-55% to -45%", min: 0.45, max: 0.55 },
    { label: "-45% to -35%", min: 0.55, max: 0.65 },
    { label: "-35% to -25%", min: 0.65, max: 0.75 },
    { label: "-25% to -15%", min: 0.75, max: 0.85 },
    { label: "-15% to -5%", min: 0.85, max: 0.95 },
    { label: "-5% to +5%", min: 0.95, max: 1.05 },
    { label: "+5% to +15%", min: 1.05, max: 1.15 },
    { label: "+15% to +25%", min: 1.15, max: 1.25 },
    { label: "+25% to +35%", min: 1.25, max: 1.35 },
    { label: "+35% to +45%", min: 1.35, max: 1.45 },
    { label: "+45% to +55%", min: 1.45, max: 1.55 },
    { label: "+55% to +65%", min: 1.55, max: 1.65 },
    { label: "+65% to +75%", min: 1.65, max: 1.75 },
    { label: "+75% to +85%", min: 1.75, max: 1.85 },
    { label: "+85% to +95%", min: 1.85, max: 1.95 },
    { label: "+95%+", min: 1.95, max: Infinity },
  ];

  const bucketCounts: Record<string, number> = Object.fromEntries(buckets.map((b) => [b.label, 0]));
  const bucketPlans: Record<string, number[]> = Object.fromEntries(buckets.map((b) => [b.label, []]));

  for (const value of values) {
    const planId: number = value[0] ?? 0;
    const diff: number = value[1] ?? 0;
    const matchedBucket = buckets.find((b) => diff > b.min && diff <= b.max);
    if (matchedBucket) {
      bucketCounts[matchedBucket.label]!++;
      bucketPlans[matchedBucket.label]?.push(planId);
    }
  }

  return {
    name: name,
    data: buckets.map((b) => bucketCounts[b.label] ?? 0),
    categories: buckets.map((b) => b.label),
    plans: bucketPlans,
  };
}

export const Route = createFileRoute("/_authenticated/user/pricing/forecast")({
  component: Forecast,
});
